import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function KidsDecodeOutput() {
  // In production, this would come from API based on child's inputs
  const childName = 'Alex'; // First name only
  const decodeData = {
    coreTemperament: 'This child has an internally-focused processing style. Experiences are taken in, held internally, and processed before being expressed outward. This is a baseline orientation, not a choice or learned behavior.',
    worldExperience: 'Sensory input is absorbed gradually rather than immediately reacted to. New environments, people, or situations are observed and assessed before engagement. This processing pace is consistent across contexts and is not situational anxiety or shyness.',
    emotionalProcessing: 'Emotions are felt deeply but expressed selectively. Internal emotional experience is rich and sustained, but outward expression is measured. This does not indicate emotional suppression or difficulty—it reflects a natural processing rhythm.',
    regulationNeeds: 'This child requires lower stimulation environments to maintain regulation. High-energy or chaotic settings create processing overload rather than excitement. Transitions require internal adjustment time. Sensitivity to environmental intensity is neurological, not behavioral.',
    misinterpretations: [{
      mistaken: 'Withdrawn or antisocial',
      actual: 'Processing internally before engaging'
    }, {
      mistaken: 'Anxious or fearful',
      actual: 'Observing and assessing before participating'
    }, {
      mistaken: 'Emotionally distant',
      actual: 'Experiencing emotions internally with selective expression'
    }],
    boundaryStatement: 'This decode does not assess behavior quality, intelligence, emotional health, or parenting effectiveness. It describes temperament and processing style only.'
  };
  return <>
      <Helmet data-id="element-1895">
        <title data-id="element-1896">Kids Decode — Output | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-1897" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1898">
        {/* Navigation */}
        <header data-id="element-1899">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-1900">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1901">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-1902">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1903" />
                <span className="text-lg font-medium tracking-tight" data-id="element-1904">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-1905">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-1906">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-1907">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-1908">
          <div className="max-w-3xl mx-auto" data-id="element-1909">
            {/* Page Header */}
            <div className="mb-16" data-id="element-1910">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1911">
                Kids Decode
              </h1>
              <p className="text-sm text-[#F5F5F5]/50 mb-2" data-id="element-1912">
                Temperament and processing style overview
              </p>
              <p className="text-sm text-[#F5F5F5]/40" data-id="element-1913">
                Generated for: {childName}
              </p>
            </div>

            {/* Section 1: Core Temperament */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1914">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1915">
                Core temperament
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1916">
                {decodeData.coreTemperament}
              </p>
            </section>

            {/* Section 2: How the Child Experiences the World */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1917">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1918">
                How this child experiences the world
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1919">
                {decodeData.worldExperience}
              </p>
            </section>

            {/* Section 3: Emotional Processing Style */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1920">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1921">
                Emotional processing style
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1922">
                {decodeData.emotionalProcessing}
              </p>
            </section>

            {/* Section 4: Regulation & Stimulation Needs */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1923">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1924">
                Regulation and stimulation needs
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1925">
                {decodeData.regulationNeeds}
              </p>
            </section>

            {/* Section 5: Common Misinterpretations */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1926">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1927">
                Common misinterpretations
              </h2>
              <div className="space-y-6" data-id="element-1928">
                {decodeData.misinterpretations.map((item, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-1929">
                    <p className="text-[#F5F5F5]/50 text-sm mb-2" data-id="element-1930">
                      May be mistaken as:
                    </p>
                    <p className="text-[#F5F5F5]/70 mb-3" data-id="element-1931">{item.mistaken}</p>
                    <p className="text-[#F5F5F5]/50 text-sm mb-2" data-id="element-1932">
                      But is actually:
                    </p>
                    <p className="text-[#F5F5F5]/70" data-id="element-1933">{item.actual}</p>
                  </div>)}
              </div>
            </section>

            {/* Section 6: Boundary Statement */}
            <section className="mb-12" data-id="element-1934">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1935">
                What this decode does not assess
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1936">
                {decodeData.boundaryStatement}
              </p>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-1937">
          <div className="max-w-7xl mx-auto text-center" data-id="element-1938">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-1939">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}